import { HttpRequest } from '@angular/common/http';
import { openDB } from 'idb';
import { environment } from 'src/environments/environment';

import { generateRandomAlphanumericString } from './generate-random-alphanumeric-string';
import { getIsoTimestringWithMicroseconds } from './get-iso-timestring-with-microseconds';

const apiEndpointsThatRequiresAuthToken: string[] = [];

const apiPathsWhichRequireStoringDataOnIndexedDb = [
  '/car/vin-information',
  '/car/add/vin-information',
  '/car/information/get/vehicle-information',
  '/car/vin-verification',
  '/car/add/vehicle-flags-data-info',
  '/car/add/vehicle-options-data-info',
  '/ratings/add/ratings',
  '/ratings/add/ratings-states',
  '/car/add/vehicle-gallery-data-info',
  '/car/add/vehicle-grading',
  '/ratings/delete/ratings-states',
  '/upload/vehicle-audio-video'
];

/**
 * This class will be an integral part of the PWA, and it is used when the device is offline.
 * This class will be responsible for receiving an API endpoint with path param, query param, body etc.
 * and return whatever response that the API was supposed to send if the device was online.
 */

export class ResolveApiToResponseLocally {
  constructor(private readonly _databaseName = 'vam-pwa', private readonly _databaseVersion = 1) {}

  private async _getDatabase() {
    return await openDB(this._databaseName, this._databaseVersion);
  }

  async resolve(httpRequest: HttpRequest<unknown>) {
    // if (this._isNativeRequest(httpRequest)) {
    switch (httpRequest.method) {
      case 'GET': {
        const datatoReturn = this._handleHttpGetRequests(httpRequest);
        return datatoReturn;
      }
      case 'POST': {
        const datatoReturn = await this._handleHttpPostRequests(httpRequest);
        return datatoReturn;
      }
      // }
    }
  }

  // private _isNativeRequest(httpRequest: HttpRequest<unknown>) {
  //   if (environment().production) {
  //     return httpRequest.url.startsWith('https://dms.my-car.com/api/');
  //   } else {
  //     return httpRequest.url.startsWith('https://dms.mycar-india.com/api/');
  //   }
  // }

  private _getApiPathFromRequest(httpRequest: HttpRequest<unknown>): string {
    const baseURL = environment().api;
    return httpRequest.url.substring(baseURL.length);
  }

  /**
   * This method will only handle HTTP GET requests
   * It will resolve data that is *already stored* in the IndexedDB
   * It will *not create* any new data
   */
  private async _handleHttpGetRequests(
    httpRequest: HttpRequest<unknown>
  ): Promise<Record<string, unknown>> {
    const apiPath = this._getApiPathFromRequest(httpRequest);
    const db = await this._getDatabase();

    switch (apiPath) {
      case '/car/get/countries': {
        const tx = db.transaction('countries', 'readonly');
        const store = tx.objectStore('countries');
        const data = await store.getAll();

        const neededData = data.map((countryDetails) => ({
          id: countryDetails.id,
          name: countryDetails.name
        }));
        await tx.done;

        return { success: true, data: neededData };
      }

      case '/car/get/states': {
        const tx = db.transaction('states', 'readonly');
        const store = tx.objectStore('states');
        const data = await store.getAll();

        const neededData = data.map((stateDetails) => ({
          id: stateDetails.id,
          name: stateDetails.name
        }));
        await tx.done;

        return { success: true, data: neededData };
      }

      case '/car/get/cities': {
        const tx = db.transaction('cities', 'readonly');
        const store = tx.objectStore('cities');
        const data = await store.getAll();

        const neededData = data.map((cityDetails) => ({
          id: cityDetails.id,
          name: cityDetails.name
        }));
        await tx.done;

        return { success: true, data: neededData };
      }

      case '/settings/information/get/assesment-type': {
        const tx = db.transaction('assessment_types', 'readonly');
        const store = tx.objectStore('assessment_types');
        const data = await store.getAll();
        await tx.done;

        const neededData = data.sort((a, b) =>
          a.assessment_type_name.localeCompare(b.assessment_type_name)
        );
        return { success: true, data: neededData };
      }

      case '/settings/information/get/make': {
        const tx = db.transaction('vehicle_make', 'readonly');
        const store = tx.objectStore('vehicle_make');
        const data = await store.getAll();

        const neededData = data
          .sort((a, b) => a.maker_name.localeCompare(b.maker_name))
          .map((vehicleMakeDetails) => ({
            make_id: vehicleMakeDetails.make_id,
            maker_name: vehicleMakeDetails.maker_name,
            maker_name_arabic: vehicleMakeDetails.maker_name_arabic,
            created_at: vehicleMakeDetails.created_at
          }));

        await tx.done;
        return { success: true, data: neededData };
      }

      case '/settings/conditions': {
        const tx = db.transaction('conditions', 'readonly');
        const store = tx.objectStore('conditions');
        const data = await store.getAll();
        const neededData = data.sort((a, b) => a.condition_name.localeCompare(b.condition_name));
        await tx.done;
        return { success: true, data: neededData };
      }

      case '/settings/information/get/engine-type': {
        const tx = db.transaction('engine_types', 'readonly');
        const store = tx.objectStore('engine_types');
        const data = await store.getAll();
        await tx.done;

        const neededData = data.sort((a, b) =>
          a.engine_type_name.localeCompare(b.engine_type_name)
        );
        return { success: true, data: neededData };
      }

      case '/settings/information/get/cc': {
        const tx = db.transaction('vehicle_engine_cc', 'readonly');
        const store = tx.objectStore('vehicle_engine_cc');
        const data = await store.getAll();
        await tx.done;
        const neededData = data.sort((a, b) => a.engine_cc_name.localeCompare(b.engine_cc_name));
        return { success: true, data: neededData };
      }

      case '/settings/information/get/estimara': {
        const tx = db.transaction('vehicle_estimara', 'readonly');
        const store = tx.objectStore('vehicle_estimara');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/services': {
        const tx = db.transaction('services', 'readonly');
        const store = tx.objectStore('services');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/vehicle-type': {
        const tx = db.transaction('vehicle_types', 'readonly');
        const store = tx.objectStore('vehicle_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/transmission': {
        const tx = db.transaction('transmission_types', 'readonly');
        const store = tx.objectStore('transmission_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/body-type': {
        const tx = db.transaction('body_types', 'readonly');
        const store = tx.objectStore('body_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/drive': {
        const tx = db.transaction('drive_types', 'readonly');
        const store = tx.objectStore('drive_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/exterior-color': {
        const tx = db.transaction('exterior_colors', 'readonly');
        const store = tx.objectStore('exterior_colors');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/interior-color': {
        const tx = db.transaction('interior_colors', 'readonly');
        const store = tx.objectStore('interior_colors');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/utility-type': {
        const tx = db.transaction('utility_types', 'readonly');
        const store = tx.objectStore('utility_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/doors': {
        const tx = db.transaction('door_types', 'readonly');
        const store = tx.objectStore('door_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/COD': {
        const tx = db.transaction('vehicle_codes', 'readonly');
        const store = tx.objectStore('vehicle_codes');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/vehicle-wheel-base': {
        const tx = db.transaction('vehicle_wheel_base', 'readonly');
        const store = tx.objectStore('vehicle_wheel_base');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/cylinders': {
        const tx = db.transaction('vehicle_cylinders', 'readonly');
        const store = tx.objectStore('vehicle_cylinders');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/uphoistery': {
        const tx = db.transaction('vehicle_upholsteries', 'readonly');
        const store = tx.objectStore('vehicle_upholsteries');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/fuel-type': {
        const tx = db.transaction('fuel_types', 'readonly');
        const store = tx.objectStore('fuel_types');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/vehicle-seats': {
        const tx = db.transaction('vehicle_seats', 'readonly');
        const store = tx.objectStore('vehicle_seats');
        const data = await store.getAll();
        await tx.done;
        const neededData = data.sort((a, b) => parseFloat(a.seat_name) - parseFloat(b.seat_name));
        return { success: true, data: neededData };
      }

      case '/settings/information/get/gears': {
        const tx = db.transaction('gears', 'readonly');
        const store = tx.objectStore('gears');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/origin': {
        const tx = db.transaction('vehicle_origin', 'readonly');
        const store = tx.objectStore('vehicle_origin');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/ownership': {
        const tx = db.transaction('ownership', 'readonly');
        const store = tx.objectStore('ownership');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/mileage-info': {
        const tx = db.transaction('mileage_info', 'readonly');
        const store = tx.objectStore('mileage_info');
        const data = await store.getAll();
        await tx.done;
        const neededData = data.sort((a, b) =>
          a.mileage_info_name.localeCompare(b.mileage_info_name)
        );
        return { success: true, data: neededData };
      }

      case '/settings/information/get/max-speeds': {
        const tx = db.transaction('max_speeds', 'readonly');
        const store = tx.objectStore('max_speeds');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/market-acceptibility': {
        const tx = db.transaction('market_acceptability', 'readonly');
        const store = tx.objectStore('market_acceptability');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/mortage': {
        const tx = db.transaction('mortgage', 'readonly');
        const store = tx.objectStore('mortgage');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/settings/information/get/warranty': {
        // implement this later: no need for evaluator
        return { success: true, data: [] };
      }

      case '/settings/information/get/horse-power': {
        // implement this later: no need for evaluator
        return { success: true, data: [] };
      }

      case '/settings/information/get/torque': {
        // implement this later: no need for evaluator
        return { success: true, data: [] };
      }

      case '/settings/information/get/acceleration': {
        // implement this later: no need for evaluator
        return { success: true, data: [] };
      }

      case '/car/get/vehicle-options': {
        const tx = db.transaction('options', 'readonly');
        const store = tx.objectStore('options');
        const data = await store.getAll();
        await tx.done;
        return { success: true, data };
      }

      case '/car/get/vehicle-flags': {
        const tx = db.transaction('vehicle_flags', 'readonly');
        const store = tx.objectStore('vehicle_flags');
        const data = (await store.getAll()).sort((a, b) => {
          if (a.flag_name < b.flag_name) return -1;
          if (a.flag_name > b.name) return 1;
          return 0;
        });
        await tx.done;
        return { success: true, data };
      }

      case '/settings/get/gallery': {
        let objectToReturn: Record<string, unknown> = {};

        const tx = db.transaction('gallery', 'readonly');
        const store = tx.objectStore('gallery');
        objectToReturn = {
          success: true,
          data: {
            gallerys: (await store.getAll()).map((el) => ({
              gallery_id: el?.['gallery_id'],
              gallery_name: el?.['gallery_name'],
              gallery_name_arabic: el?.['gallery_name_arabic'],
              gallery_placeholder: 'N/A'
            })),
            url: 'N/A'
          }
        };
        await tx.done;
        return objectToReturn;
      }

      default: {
        console.log('inside ResolveApiToResponseLocally.resolve::GET - default block');
        // console.log('apiPath is:', apiPath);
      }
    }
  }

  private async _handleHttpPostRequests(
    httpRequest: HttpRequest<unknown>
  ): Promise<Record<string, unknown>> {
    const apiPath = this._getApiPathFromRequest(httpRequest);
    const db = await this._getDatabase();

    if (apiPathsWhichRequireStoringDataOnIndexedDb.includes(apiPath)) {
      switch (apiPath) {
        case '/car/vin-information': {
          const vinNumber = (httpRequest.body as any).vin_number;
          this._setCurrentLocalVinId(vinNumber);
          const randomVehicleIdForLocal = generateRandomAlphanumericString();

          const currentIsoTimeWithMicroseconds = getIsoTimestringWithMicroseconds();

          await this._setupTempDataStructureForThisUser({
            vin: vinNumber
          });

          await this._storeApiCallForVin({
            httpRequest: httpRequest,
            endpoint: apiPath,
            vin: vinNumber,
            group: 'registration'
          });

          await this._storeMapperForVin({
            mapperValueObject: {
              vehicleId: {
                local: randomVehicleIdForLocal,
                remote: null
              }
            },
            vin: vinNumber
          });

          await this._storeLocalTempData({
            vin: vinNumber,
            localTempDataValueObject: {
              metadata: {
                createdBy: this._getCurrentlyLoggedInUserId(),
                updatedBy: this._getCurrentlyLoggedInUserId()
              },
              vehicleRegistrationData: {
                vehicleId: randomVehicleIdForLocal,
                vinNumber: vinNumber,
                plateNumber: (httpRequest.body as any)?.['plate_number'],
                location: (httpRequest.body as any)?.['location'],
                city: (httpRequest.body as any)?.['city'],
                area: (httpRequest.body as any)?.['area'],
                createdAt: currentIsoTimeWithMicroseconds,
                updatedAt: currentIsoTimeWithMicroseconds
              }
            }
          });

          const objectToReturn = {
            success: true,
            vehicle_id: randomVehicleIdForLocal,
            data: 'Vehicle Details Insertion Successfully!'
          };

          return objectToReturn;
        }

        case '/car/information/get/vehicle-information': {
          const informationRegardingCurrentVin = await this._getInformationAboutTheCurrentVin();

          const currentVehicleId = await this._getCurrentVehicleId();
          const currentLocalVinId = this._getCurrentLocalVinId();

          const vehicleRegistrationData =
            informationRegardingCurrentVin?.['vehicleRegistrationData'];
          const vehicleInformationData = informationRegardingCurrentVin?.['vehicleInformationData'];
          const vehicleOptionsData = informationRegardingCurrentVin?.['vehicleOptionsData'];

          const vehicleFlagsData = informationRegardingCurrentVin?.['vehicleFlagsData'];
          const vehicleGalleryData = informationRegardingCurrentVin?.['vehicleGalleryData'];

          const localInformationMetadata = informationRegardingCurrentVin?.['metadata'];

          let vinImage = undefined;
          let plateImage = undefined;
          let odoImage = undefined;

          let recordedAudioFile = undefined;
          let recordedVideoFile = undefined;

          if (vehicleRegistrationData?.['vinImage']) {
            vinImage = await this._getImageFromImageStore({
              imageId: vehicleRegistrationData?.['vinImage'],
              get: 'thumbnail'
            });
          }

          if (vehicleRegistrationData?.['plateImage']) {
            plateImage = await this._getImageFromImageStore({
              imageId: vehicleRegistrationData?.['plateImage'],
              get: 'thumbnail'
            });
          }
          if (vehicleRegistrationData?.['odoImage']) {
            odoImage = await this._getImageFromImageStore({
              imageId: vehicleRegistrationData?.['odoImage'],
              get: 'thumbnail'
            });
          }

          if (vehicleInformationData?.['audio_file_path']) {
            recordedAudioFile = await this._getAudioFromAudioStore({
              audioId: vehicleInformationData?.['audio_file_path']
            });
          }

          if (vehicleInformationData?.['video_file_path']) {
            recordedVideoFile = await this._getVideoFromAudioStore({
              videoId: vehicleInformationData?.['video_file_path']
            });
          }

          const objectToReturn = {
            success: true,
            data: {
              vehicle_information: vehicleInformationData
                ? {
                    vehicle_information_id: vehicleInformationData?.['vehicleInformationId'],
                    vehicle_id: vehicleInformationData?.['vehicleId'],
                    assessment_type_id: vehicleInformationData?.['assessmentTypeId'],
                    make_id: vehicleInformationData?.['makeId'],
                    engine_type_id: vehicleInformationData?.['engineTypeId'],
                    vendor_id: vehicleInformationData?.['vendorId'],
                    model_id: vehicleInformationData?.['modelId'],
                    estimara_id: vehicleInformationData?.['estimaraId'],
                    engine_cc_id: vehicleInformationData?.['engineCcId'],
                    trim_id: vehicleInformationData?.['trimId'],
                    cyllinder_id: vehicleInformationData?.['cylinderId'],
                    vehicle_type_id: vehicleInformationData?.['vehicleTypeId'],
                    transmission_type_id: vehicleInformationData?.['transmissionTypeId'],
                    body_type_id: vehicleInformationData?.['bodyTypeId'],
                    drive_type_id: vehicleInformationData?.['driveTypeId'],
                    warranty_id: vehicleInformationData?.['warrantyId'],
                    wheel_base_id: vehicleInformationData?.['wheelBaseId'],
                    exterior_color_id: vehicleInformationData?.['exteriorColorId'],
                    interior_color_id: vehicleInformationData?.['interiorColorId'],
                    warranty_expiry_date: vehicleInformationData?.['warrantyExpiryDate'],
                    odo: vehicleInformationData?.['odo'],
                    last_service_date: vehicleInformationData?.['lastServiceDate'],
                    utility_type_id: vehicleInformationData?.['utilityTypeId'],
                    door_type_id: vehicleInformationData?.['doorTypeId'],
                    last_service_km: vehicleInformationData?.['lastServiceKm'],
                    vehicle_condition_id: vehicleInformationData?.['vehicleConditionId'],
                    fuel_type_id: vehicleInformationData?.['fuelTypeId'],
                    uphoistery_id: vehicleInformationData?.['uphoisteryId'],
                    key_type_id: vehicleInformationData?.['keyTypeId'], // <- check later
                    power_id: vehicleInformationData?.['powerId'],
                    torque_id: vehicleInformationData?.['torqueId'],
                    acceleration_id: vehicleInformationData?.['accelerationId'],
                    cod_id: vehicleInformationData?.['codId'],
                    vehicle_location: vehicleInformationData?.['vehicleLocation'], // <- check later
                    mileage: vehicleInformationData?.['mileageId'],
                    created_at: vehicleInformationData?.['createdAt'],
                    updated_at: vehicleInformationData?.['updatedAt'],
                    created_by: vehicleInformationData?.['createdBy'],
                    updated_by: vehicleInformationData?.['updatedBy'],
                    seat_id: vehicleInformationData?.['seatId'],
                    gear_id: vehicleInformationData?.['gearId'],
                    vehicle_item_code: vehicleInformationData?.['vehicleItemCode'],
                    overall_exterior: vehicleInformationData?.['overallExterior'], // <- check later
                    overall_interior: vehicleInformationData?.['overallInterior'], // <- check later
                    overall_mechanics: vehicleInformationData?.['overallMechanics'], // <- check later
                    overall_frame: vehicleInformationData?.['overallFrame'], // <- check later
                    overall_major_options: vehicleInformationData?.['overallMajorOptions'], // <- check later
                    perceived_value: vehicleInformationData?.['perceivedValue'], // <- check later
                    calculated_value: vehicleInformationData?.['calculatedValue'], // <- check later
                    overall_rating: vehicleInformationData?.['overallRating'], // <- check later
                    overall_repair_cost: vehicleInformationData?.['overall_repair_cost'], // <- check later
                    year_id: vehicleInformationData?.['modelYearId'],
                    service_maintainance: vehicleInformationData?.['serviceMaintainance'],
                    max_speed: vehicleInformationData?.['maxSpeedId'],
                    vehicle_origin_id: vehicleInformationData?.['vehicleOriginId'],
                    mortage_id: vehicleInformationData?.['mortageId'],
                    ownership_id: vehicleInformationData?.['ownershipId'],
                    mileage_id: vehicleInformationData?.['mileageId'],
                    contract_number: vehicleInformationData?.['contractNumber'],
                    market_acceptabilitie_id: vehicleInformationData?.['marketAcceptabilityId'],
                    alert_pm: vehicleInformationData?.['alert_pm'], // <- check later
                    alert_sm: vehicleInformationData?.['alert_sm'], // <- check later
                    pm_approved: vehicleInformationData?.['pm_approved'], // <- check later
                    sm_approved: vehicleInformationData?.['sm_approved'], // <- check later
                    vehicle_auction_data: vehicleInformationData?.['vehicle_auction_data'], // <- check later
                    social_media: vehicleInformationData?.['social_media'], // <- check later
                    audio_file_path: recordedAudioFile ?? null, // <- check later
                    video_file_path: recordedVideoFile ?? null // <- check later
                  }
                : null,
              vehicle_options_data: vehicleOptionsData
                ? {
                    more_information: vehicleOptionsData?.['more_information'],
                    vehicle_id: currentVehicleId,
                    options: vehicleOptionsData?.['options']
                  }
                : null,
              vehicle_flags_data: vehicleFlagsData
                ? {
                    vehicle_id: this._getCurrentVehicleId,
                    flags: {
                      airbag_deployed: vehicleFlagsData?.['airbagDeployed'],
                      airbag_repaired: vehicleFlagsData?.['airbagRepaired'],
                      alloy_not_original: vehicleFlagsData?.['alloyNotOriginal'],
                      altered_exhaust: vehicleFlagsData?.['alteredExhaust'],
                      altered_suspension: vehicleFlagsData?.['alteredSuspension'],
                      'chassis_damage_(_front_)': vehicleFlagsData?.['chassisDamageFront'],
                      'chassis_damage_(_rear_)': vehicleFlagsData?.['chassisDamageRear'],
                      engine_changed: vehicleFlagsData?.['engineChanged'],
                      engine_jammed: vehicleFlagsData?.['engineJammed'],
                      engine_not_starting: vehicleFlagsData?.['engineNotStarting'],
                      engine_over_heating: vehicleFlagsData?.['engineOverHeating'],
                      flood_damaged: vehicleFlagsData?.['floodDamaged'],
                      front__moderate_accident: vehicleFlagsData?.['frontModerateAccident'],
                      front_major__accident: vehicleFlagsData?.['frontMajorAccident'],
                      front_minor_accident: vehicleFlagsData?.['frontMinorAccident'],
                      full_service_history: vehicleFlagsData?.['fullServiceHistory'],
                      'gcc_plate_n/a': vehicleFlagsData?.['gccPlateNA'],
                      hail__storm_damaged: vehicleFlagsData?.['hailStormDamaged'],
                      individual_vehicle: vehicleFlagsData?.['individualVehicle'],
                      junk: vehicleFlagsData?.['junk'],
                      lady_driven: vehicleFlagsData?.['ladyDriven'],
                      no_engine: vehicleFlagsData?.['noEngine'],
                      no_spare_key: vehicleFlagsData?.['noSpareKey'],
                      non_starting_vehicle: vehicleFlagsData?.['nonStartingVehicle'],
                      'non-gcc_vehicle': vehicleFlagsData?.['nonGCCVehicle'],
                      number_plate_missing: vehicleFlagsData?.['numberPlateMissing'],
                      odometer_rolled_back: vehicleFlagsData?.['odometerRolledBack'],
                      owner_manual_available: vehicleFlagsData?.['ownerManualAvailable'],
                      partial_service_history: vehicleFlagsData?.['partialServiceHistory'],
                      rear_accident_major: vehicleFlagsData?.['rearAccidentMajor'],
                      rear_accident_minor: vehicleFlagsData?.['rearAccidentMinor'],
                      rear_accident_moderate: vehicleFlagsData?.['rearAccidentModerate'],
                      remote_not_available: vehicleFlagsData?.['remoteNotAvailable'],
                      sandstorm_paint_damage: vehicleFlagsData?.['sandstormPaintDamage'],
                      spare_key_available: vehicleFlagsData?.['spareKeyAvailable'],
                      uni_body_damage: vehicleFlagsData?.['uniBodyDamage'],
                      vehicle_doors_locked: vehicleFlagsData?.['vehicleDoorsLocked'],
                      vehicle_fully_alternated: vehicleFlagsData?.['vehicleFullyAlternated'],
                      vehicle_roll_over: vehicleFlagsData?.['vehicleRollOver'],
                      warranty_book_available: vehicleFlagsData?.['warrantyBookAvailable']
                    }
                  }
                : null,
              vehicle_gallery_data: vehicleGalleryData
                ? {
                    vehicle_id: this._getCurrentVehicleId(),
                    parts_images: Object.values(vehicleGalleryData)?.map((d) => ({
                      part_id: d?.['part_id'],
                      part_name: d?.['part_name'],
                      image: d?.['vehicle_gallery_data']
                    }))
                  }
                : null,
              vehicle_details: {
                vehicle_id: currentVehicleId,
                vin_number: currentLocalVinId,
                plate_number: vehicleRegistrationData?.['plateNumber'],
                location: vehicleRegistrationData?.['location'],
                city: vehicleRegistrationData?.['city'],
                area: vehicleRegistrationData?.['area'],
                vin_image: vinImage,
                plate_image: plateImage,
                estimara_expiry_date: null,
                estimara_image: vehicleRegistrationData?.['estimaraImage'],
                mvpi_expiry_date: null,
                mvpi_image: null,
                odo_value: vehicleRegistrationData?.['odoValue'],
                odo_image: odoImage,
                created_at: vehicleRegistrationData?.['createdAt'],
                updated_at: vehicleRegistrationData?.['updatedAt'],
                created_by: localInformationMetadata?.['createdBy'],
                updated_by: localInformationMetadata?.['updatedBy'],
                push_type: null,
                push_id: null,
                is_spin_car: 0,
                is_featured: 0,
                is_auction: 0,
                is_retail: 0,
                product_id: null,
                test_drive_video: null,
                auction_status: null,
                blocked_status: null,
                auction_bid_price: null,
                auction_push_date: null,
                retail_push_date: null,
                new_vehicle: null,
                evaluated_date: '2024-12-24 09:45:30',
                evaluated_by: localInformationMetadata?.['createdBy'],
                repair_panels: vehicleInformationData?.['repairPanels'],
                vehicle_walkin: null,
                vehicle_quotations: null
              }
            }
          };

          return objectToReturn;
        }

        case '/upload/vehicle-audio-video': {
          console.log('inside /upload/vehicle-audio-video');
          const convertedObjectFromFormdata = await this._convertFormDataToJsonV2(httpRequest.body);
          console.log('convertedObjectFromFormdata: ', convertedObjectFromFormdata);

          const keyValuePairsWhereValuesAreBinaryFiles =
            await this._getKeyValuePairsWhereValuesAreBinaryFile(httpRequest.body);

          console.log(
            'keyValuePairsWhereValuesAreBinaryFiles: ',
            keyValuePairsWhereValuesAreBinaryFiles
          );

          await this._storeApiCallForVin({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: 'information',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ],
            keyValuePairsWhereValuesAreBinaryFiles
          });

          let localObjectForUpdate = {};
          let typeOfFile: 'Audio' | 'Video' | undefined = undefined;

          if (keyValuePairsWhereValuesAreBinaryFiles?.['audio']) {
            typeOfFile = 'Audio';
            localObjectForUpdate['audio_file_path'] =
              keyValuePairsWhereValuesAreBinaryFiles?.['audio'] ?? null;
          }

          if (keyValuePairsWhereValuesAreBinaryFiles?.['video']) {
            typeOfFile = 'Video';
            localObjectForUpdate['video_file_path'] =
              keyValuePairsWhereValuesAreBinaryFiles?.['video'] ?? null;
          }

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehicleInformationData: localObjectForUpdate
            }
          });

          let dataUrlToSend: string = '';

          switch (typeOfFile) {
            case 'Audio': {
              dataUrlToSend = await this._getAudioFromAudioStore({
                audioId: keyValuePairsWhereValuesAreBinaryFiles?.['audio']
              });
              break;
            }
            case 'Video': {
              dataUrlToSend = await this._getVideoFromAudioStore({
                videoId: keyValuePairsWhereValuesAreBinaryFiles?.['video']
              });
            }
          }

          const objectToReturn = {
            success: true,
            message: 'File uploaded and compressed successfully',
            url: dataUrlToSend
          };
          return objectToReturn;
        }

        case '/car/vin-verification': {
          const convertedObjectFromFormdata = await this._convertFormDataToJsonV2(httpRequest.body);
          const keyValuePairsWhereValuesAreBinaryFiles =
            await this._getKeyValuePairsWhereValuesAreBinaryFile(httpRequest.body);

          await this._storeApiCallForVin({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: 'registration',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ],
            keyValuePairsWhereValuesAreBinaryFiles
          });

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehicleRegistrationData: {
                estimaraExpiryDate: convertedObjectFromFormdata?.['estimara_expiry_date'],
                estimaraImage: keyValuePairsWhereValuesAreBinaryFiles?.['estimara_image'],
                mvpiExpiryDate: convertedObjectFromFormdata?.['mvpi_expiry_date'],
                mvpiImage: keyValuePairsWhereValuesAreBinaryFiles?.['mvpi_image'],
                odoValue: convertedObjectFromFormdata?.['odo_value'],
                odoImage: keyValuePairsWhereValuesAreBinaryFiles?.['odo_image'],
                plateImage: keyValuePairsWhereValuesAreBinaryFiles?.['plate_image'],
                vinImage: keyValuePairsWhereValuesAreBinaryFiles?.['vin_image'],
                updatedAt: getIsoTimestringWithMicroseconds()
              }
            }
          });

          const objectToReturn = {
            success: true,
            data: 'Vehicle Verification Updated Succssfully!'
          };
          return objectToReturn;
        }

        case '/car/add/vehicle-options-data-info': {
          await this._storeApiCallForVin({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: 'options',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ]
          });

          const parsedRequestBodyObject = JSON.parse(
            (httpRequest.body as any).vehicle_options_data
          );

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehicleOptionsData: {
                ...parsedRequestBodyObject
              }
            }
          });

          const informationRegardingCurrentVin = await this._getInformationAboutTheCurrentVin();
          const objectToReturn = {
            success: true,
            vehicle_id: informationRegardingCurrentVin['vehicleRegistrationData']['vehicle_id'],
            data: 'Vehicle Options Data Insertion Successfully!'
          };
          return objectToReturn;
        }

        case '/car/add/vin-information': {
          let objectToReturn = {};

          /**
           * This API is getting called in 2 ways: one with `repair_panels` in the request body (in ratings tab)
           * and the other at the `information` tab with a very large object in the request body
           */
          const isThisApiCallForRepairPanels = httpRequest.body?.['repair_panels'] !== undefined;

          if (isThisApiCallForRepairPanels) {
            await this._storeApiCallForVin({
              httpRequest: httpRequest,
              endpoint: apiPath,
              group: 'information',
              keysToBeReplacedInApiRequestBody: [
                {
                  localKey: 'vehicleId',
                  remoteKey: 'vehicle_id'
                }
              ]
            });

            await this._storeLocalTempData({
              localTempDataValueObject: {
                vehicleInformationData: {
                  repairPanels: httpRequest.body?.['repair_panels']
                }
              }
            });

            objectToReturn = {
              success: true,
              data: 'Vehicle Informations Updated Succssfully!'
            };
          } else {
            const vehicleInformationId = generateRandomAlphanumericString();
            await this._storeMapperForVin({
              mapperValueObject: {
                vehicleInformationId: {
                  local: vehicleInformationId,
                  remote: null
                }
              }
            });

            await this._storeApiCallForVin({
              httpRequest: httpRequest,
              endpoint: apiPath,
              group: 'information',
              keysToBeReplacedInApiRequestBody: [
                {
                  localKey: 'vehicleId',
                  remoteKey: 'vehicle_id'
                }
              ]
            });

            const requestBody = httpRequest.body as any;

            await this._storeLocalTempData({
              localTempDataValueObject: {
                vehicleInformationData: {
                  accelerationId: requestBody?.['acceleration_id'],
                  assessmentTypeId: requestBody?.['assessment_type_id'],
                  bodyTypeId: requestBody?.['body_type_id'],
                  codId: requestBody?.['cod_id'],
                  contractNumber: requestBody?.['contract_number'],
                  cylinderId: requestBody?.['cyllinder_id'],
                  createdAt: getIsoTimestringWithMicroseconds(),
                  createdBy: this._getCurrentlyLoggedInUserId(),
                  doorTypeId: requestBody?.['door_type_id'],
                  driveTypeId: requestBody?.['drive_type_id'],
                  engineCcId: requestBody?.['engine_cc_id'],
                  engineTypeId: requestBody?.['engine_type_id'],
                  estimaraExpiryDate: requestBody?.['estimara_expiry_date'],
                  estimaraId: requestBody?.['estimara_id'],
                  exteriorColorId: requestBody?.['exterior_color_id'],
                  externalEstimator: requestBody?.['external_estimator'],
                  fuelTypeId: requestBody?.['fuel_type_id'],
                  gearId: requestBody?.['gear_id'],
                  interiorColorId: requestBody?.['interior_color_id'],
                  lastServiceDate: requestBody?.['last_service_date'],
                  lastServiceKm: requestBody?.['last_service_km'],
                  makeId: requestBody?.['make_id'],
                  marketAcceptabilityId: requestBody?.['market_acceptabilitie_id'],
                  maxSpeedId: requestBody?.['max_speed'],
                  mileageId: requestBody?.['mileage_id'],
                  modelId: requestBody?.['model_id'],
                  modelYearId: requestBody?.['model_year_id'],
                  mortageId: requestBody?.['mortage_id'],
                  mvpiExpiryDate: requestBody?.['mvpi_expiry_date'],
                  odo: requestBody?.['odo'],
                  ownershipId: requestBody?.['ownership_id'],
                  plateNumber: requestBody?.['plate_number'],
                  powerId: requestBody?.['power_id'],
                  seatId: requestBody?.['seat_id'],
                  serviceMaintainance: requestBody?.['service_maintainance'],
                  testDriveVideo: requestBody?.['test_drive_video'],
                  torqueId: requestBody?.['torque_id'],
                  transmissionTypeId: requestBody?.['transmission_type_id'],
                  trimId: requestBody?.['trim_id'],
                  updatedAt: getIsoTimestringWithMicroseconds(),
                  updatedBy: this._getCurrentlyLoggedInUserId(),
                  uphoisteryId: requestBody?.['uphoistery_id'],
                  userId: requestBody?.['user_id'],
                  utilityTypeId: requestBody?.['utility_type_id'],
                  vehicleConditionId: requestBody?.['vehicle_condition_id'],
                  vehicleId: requestBody?.['vehicle_id'],
                  vehicleInformationId,
                  vehicleItemCode: requestBody?.['vehicle_item_code'],
                  vehicleOriginId: requestBody?.['vehicle_origin_id'],
                  vehicleTypeId: requestBody?.['vehicle_type_id'],
                  vinNumber: requestBody?.['vin_number'],
                  vendorId: requestBody?.['vendor_id'],
                  warrantyExpiryDate: requestBody?.['warranty_expiry_date'],
                  warrantyId: requestBody?.['warranty_id'],
                  wheelBaseId: requestBody?.['wheel_base_id']
                }
              }
            });

            objectToReturn = {
              success: true,
              data: 'Vehicle Informations Updated Succssfully!'
            };
          }

          return objectToReturn;
        }

        case '/car/add/vehicle-flags-data-info': {
          await this._storeApiCallForVin({
            endpoint: apiPath,
            httpRequest: httpRequest,
            group: 'flags',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ]
          });

          const parsedData = JSON.parse((httpRequest.body as any)?.['vehicle_flags_data']);

          const vehicleFlagsData = parsedData?.['flags'];

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehicleFlagsData: {
                airbagRepaired: vehicleFlagsData?.['airbag_repaired'],
                airbagDeployed: vehicleFlagsData?.['airbag_deployed'],
                alloyNotOriginal: vehicleFlagsData?.['alloy_not_original'],
                alteredExhaust: vehicleFlagsData?.['altered_exhaust'],
                alteredSuspension: vehicleFlagsData?.['altered_suspension'],
                chassisDamageFront: vehicleFlagsData?.['chassis_damage_(_front_)'],
                chassisDamageRear: vehicleFlagsData?.['chassis_damage_(_rear_)'],
                engineChanged: vehicleFlagsData?.['engine_changed'],
                engineJammed: vehicleFlagsData?.['engine_jammed'],
                engineNotStarting: vehicleFlagsData?.['engine_not_starting'],
                engineOverHeating: vehicleFlagsData?.['engine_over_heating'],
                floodDamaged: vehicleFlagsData?.['flood_damaged'],
                frontModerateAccident: vehicleFlagsData?.['front__moderate_accident'],
                frontMajorAccident: vehicleFlagsData?.['front_major__accident'],
                frontMinorAccident: vehicleFlagsData?.['front_minor_accident'],
                fullServiceHistory: vehicleFlagsData?.['full_service_history'],
                gccPlateNA: vehicleFlagsData?.['gcc_plate_n/a'],
                hailStormDamaged: vehicleFlagsData?.['hail__storm_damaged'],
                individualVehicle: vehicleFlagsData?.['individual_vehicle'],
                junk: vehicleFlagsData?.['junk'],
                ladyDriven: vehicleFlagsData?.['lady_driven'],
                noEngine: vehicleFlagsData?.['no_engine'],
                noSpareKey: vehicleFlagsData?.['no_spare_key'],
                nonStartingVehicle: vehicleFlagsData?.['non_starting_vehicle'],
                nonGCCVehicle: vehicleFlagsData?.['non-gcc_vehicle'],
                numberPlateMissing: vehicleFlagsData?.['number_plate_missing'],
                odometerRolledBack: vehicleFlagsData?.['odometer_rolled_back'],
                ownerManualAvailable: vehicleFlagsData?.['owner_manual_available'],
                partialServiceHistory: vehicleFlagsData?.['partial_service_history'],
                rearAccidentMajor: vehicleFlagsData?.['rear_accident_major'],
                rearAccidentMinor: vehicleFlagsData?.['rear_accident_minor'],
                rearAccidentModerate: vehicleFlagsData?.['rear_accident_moderate'],
                remoteNotAvailable: vehicleFlagsData?.['remote_not_available'],
                sandstormPaintDamage: vehicleFlagsData?.['sandstorm_paint_damage'],
                spareKeyAvailable: vehicleFlagsData?.['spare_key_available'],
                uniBodyDamage: vehicleFlagsData?.['uni_body_damage'],
                vehicleRollOver: vehicleFlagsData?.['vehicle_roll_over'],
                vehicleDoorsLocked: vehicleFlagsData?.['vehicle_doors_locked'],
                vehicleFullyAlternated: vehicleFlagsData?.['vehicle_fully_alternated'],
                warrantyBookAvailable: vehicleFlagsData?.['warranty_book_available']
              }
            }
          });

          const objectToReturn = {
            success: true,
            vehicle_id: await this._getCurrentVehicleId(),
            data: 'Vehicle Flags Data Insertion Successfully!'
          };
          return objectToReturn;
        }

        case '/ratings/add/ratings': {
          const tx = db.transaction('part_condition', 'readonly');
          const store = tx.objectStore('part_condition');
          const data = await store.getAll();
          const convertedObjectFromFormdata = await this._convertFormDataToJson(
            httpRequest.body as FormData
          );

          const parentPartId = convertedObjectFromFormdata?.['parent_part_id'] as string;
          let group: 'exterior' | 'interior' | 'mechanics' | 'frame' | 'major_options' = 'exterior';
          let breakAllLoops = false;

          // this loop is calculating which group this api call should be caterogized into
          for (let i = 0; i < data.length; i++) {
            const d = data[i];
            const partsConditionsObject = d?.['partsConditions'];
            const keys = Object.keys(partsConditionsObject);
            for (let j = 0; j < keys.length; j++) {
              const key = keys[j];
              const value = partsConditionsObject[key];
              for (let k = 0; k < value.length; k++) {
                const valueData = value[k];

                if (
                  valueData?.['ratings']?.length !== 0 &&
                  parentPartId === valueData?.['parts_condition_id']
                ) {
                  breakAllLoops = true;
                  group = valueData?.['parts_condition_type'];
                }

                if (breakAllLoops) break;
              }
              if (breakAllLoops) break;
            }
            if (breakAllLoops) break;
          }

          const keyValuePairsWhereValuesAreBinaryFiles =
            await this._getKeyValuePairsWhereValuesAreBinaryFile(httpRequest.body);

          await this._storeApiCallForRatingsData({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: group,
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ],
            requiresAuthToken: true,
            keyValuePairsWhereValuesAreBinaryFiles
          });

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehiclePartsRatingConditionsData: {
                [parentPartId]: {
                  parent_part_id: convertedObjectFromFormdata?.['parent_part_id'],
                  parts_conditions_id: convertedObjectFromFormdata?.['parts_conditions_id'],
                  vehicle_id: convertedObjectFromFormdata?.['vehicle_id'],
                  vpcs_lb_cost: convertedObjectFromFormdata?.['vpcs_lb_cost'],
                  vpcs_new_image1: keyValuePairsWhereValuesAreBinaryFiles?.['vpcs_new_image1'],
                  vpcs_new_image2: keyValuePairsWhereValuesAreBinaryFiles?.['vpcs_new_image2'],
                  vpcs_old_image1: keyValuePairsWhereValuesAreBinaryFiles?.['vpcs_old_image1'],
                  vpcs_old_image2: keyValuePairsWhereValuesAreBinaryFiles?.['vpcs_old_image2'],
                  vpcs_prt_cost: convertedObjectFromFormdata?.['vpcs_prt_cost'],
                  vpcs_rp_cost: convertedObjectFromFormdata?.['vpcs_rp_cost'],
                  vpcs_s_dec: convertedObjectFromFormdata?.['vpcs_s_dec'],
                  vpcs_severity: convertedObjectFromFormdata?.['vpcs_severity']
                }
              }
            }
          });

          const objectToReturn = {
            success: true,
            data: 'Ratings Added Sucessfully'
          };
          return objectToReturn;
        }

        case '/ratings/add/ratings-states': {
          // this API does not have the FormData containing images
          const ratingStates = JSON.parse((httpRequest.body as any)?.['rating_states']);

          await this._storeApiCallForRatingsData({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: 'generic',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ]
          });

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehicleRatingsData: ratingStates
            }
          });

          const objectToReturn = {
            success: true,
            data: 'Rating States Added Sucessfully'
          };
          return objectToReturn;
        }

        case '/car/add/vehicle-grading': {
          let objectToReturn = {};

          await this._storeApiCallForRatingsData({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: 'grading',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ]
          });

          objectToReturn = {
            success: true,
            data: 'Vehicle Gradings Data Updated Succssfully!'
          };

          return objectToReturn;
        }

        case '/car/add/vehicle-gallery-data-info': {
          let objectToReturn = {};

          const keyValuePairsWhereValuesAreBinaryFiles =
            await this._getKeyValuePairsWhereValuesAreBinaryFile(httpRequest.body);

          await this._storeApiCallForVin({
            httpRequest: httpRequest,
            endpoint: apiPath,
            group: 'gallery',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ],
            keyValuePairsWhereValuesAreBinaryFiles
          });

          const convertedObjectFromFormdata = await this._convertFormDataToJson(
            httpRequest.body as FormData
          );

          // const partId = (convertedObjectFromFormdata?.['part_id'] as string)

          let partId;

          if ((convertedObjectFromFormdata?.['part_id'] as string).trim().length === 0) {
            partId = `user-created-${generateRandomAlphanumericString()}`;
          } else {
            partId = convertedObjectFromFormdata?.['part_id'] as string;
          }

          await this._storeLocalTempData({
            localTempDataValueObject: {
              vehicleGalleryData: {
                [partId]: {
                  part_id: partId,
                  part_name: convertedObjectFromFormdata?.['part_name'],
                  vehicle_gallery_data:
                    keyValuePairsWhereValuesAreBinaryFiles?.['vehicle_gallery_data'],
                  vehicle_id: convertedObjectFromFormdata?.['vehicle_id']
                }
              }
            }
          });

          objectToReturn = {
            success: true,
            data: 'Vehicle Gallery Data Updated Succssfully!'
          };

          return objectToReturn;
        }

        case '/ratings/delete/ratings-states': {
          let objectToReturn = {};

          const vinId = this._getCurrentLocalVinId();
          const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();

          const db = await this._getDatabase();
          const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
          const store = tx.objectStore('VAM-PWA-API-CALLS');

          const previousRecord = await store.get(vinId);

          const newRecord = {
            ...previousRecord,
            [currentlyLoggedInUserId]: {
              ...(previousRecord?.[currentlyLoggedInUserId] ?? {}),
              apiCallsData: {
                ...(previousRecord?.[currentlyLoggedInUserId]?.['apiCallsData'] ?? {}),
                ratings: {
                  exterior: null,
                  frame: null,
                  generic: null,
                  interior: null,
                  major_options: null,
                  mechanics: null
                }
              },
              localTempData: {
                ...(previousRecord?.[currentlyLoggedInUserId]?.['localTempData'] ?? {}),
                vehicleRatingsData: null
              }
            }
          };

          await store.put(newRecord);
          await tx.done;

          await this._storeApiCallForVin({
            httpRequest: httpRequest,
            endpoint: apiPath,
            vin: vinId,
            group: 'information',
            keysToBeReplacedInApiRequestBody: [
              {
                localKey: 'vehicleId',
                remoteKey: 'vehicle_id'
              }
            ]
          });

          objectToReturn = {
            success: true,
            data: 'Ratings deleted successfully'
          };

          return objectToReturn;
        }

        default: {
          console.log('[832135]inside ResolveApiToResponseLocally.resolve::POST - default block');
          console.log('apiPath is:', apiPath);
        }
      }
    } else {
      switch (apiPath) {
        case '/car/get/location': {
          const tx = db.transaction('cities', 'readonly');
          const store = tx.objectStore('cities');
          const cityId = (httpRequest.body as any).city_id;
          const cityDetail = await store.get(cityId);

          const neededData = cityDetail.areas.map((area: any) => ({
            area_id: area.area_id,
            city_id: area.city_id,
            area_name: area.area_name,
            area_name_arabic: area.area_name_arabic
          }));
          await tx.done;
          return { success: true, data: neededData };
        }

        case '/filters/information/get/model-year': {
          const tx = db.transaction('vehicle_make', 'readonly');
          const store = tx.objectStore('vehicle_make');
          const makeId = (httpRequest.body as any).make_id;
          const data = await store.get(makeId);
          const neededData = data.years.map((yearInformation: any) => ({
            year_id: yearInformation.year_id,
            year_value: yearInformation.year_value
          }));
          await tx.done;
          return { success: true, data: neededData };
        }

        case '/filters/information/get/model': {
          const tx = db.transaction('vehicle_make', 'readonly');
          const store = tx.objectStore('vehicle_make');

          const makeId = (httpRequest.body as any).make_id;
          const yearId = (httpRequest.body as any).year_id;

          const data = await store.get(makeId);

          const neededData = [];

          for (let i = 0; i < data.years.length; i++) {
            if (data.years[i].year_id === yearId) {
              for (let j = 0; j < data.years[i].vehicle_models.length; j++) {
                const vehicleModelInformation = data.years[i].vehicle_models[j];

                const obj = {
                  model_id: vehicleModelInformation.model_id,
                  make_id: vehicleModelInformation.make_id,
                  type_id: vehicleModelInformation.type_id,
                  year_id: vehicleModelInformation.year_id,
                  model_name: vehicleModelInformation.model_name,
                  model_name_arabic: vehicleModelInformation.model_name_arabic
                };

                neededData.push(obj);
              }
            }
          }

          await tx.done;

          return { success: true, data: neededData };
        }

        case '/filters/information/get/model-trims': {
          const tx = db.transaction('vehicle_make', 'readonly');
          const store = tx.objectStore('vehicle_make');

          const modelId = (httpRequest.body as any).model_id;

          const neededData = [];

          let cursor = await store.openCursor();

          let breakAllLoops = false;

          while (cursor) {
            for (let i = 0; i < cursor.value.years.length; i++) {
              const yearDetails = cursor.value.years[i];
              for (let j = 0; j < yearDetails.vehicle_models.length; j++) {
                const vehicleModelInformation = yearDetails.vehicle_models[j];
                if (vehicleModelInformation.model_id === modelId) {
                  neededData.push(...vehicleModelInformation.modle_trim);
                  breakAllLoops = true;
                  break;
                }
              }
              if (breakAllLoops) break;
            }
            if (breakAllLoops) {
              cursor = null;
            } else {
              cursor = await cursor.continue();
            }
          }

          await tx.done;
          return { success: true, data: neededData };
        }

        case '/car/get/vehicle-status': {
          const informationRegardingCurrentVin = await this._getInformationAboutTheCurrentVin();

          const isThereDataInsertedInInformationPage =
            informationRegardingCurrentVin?.['vehicleInformationData'] !== null ? true : false;
          const isThereDataInsertedInOptionsPage =
            informationRegardingCurrentVin?.['vehicleOptionsData'] !== null ? true : false;
          const isThereDataInsertedInFlagsPage =
            informationRegardingCurrentVin?.['vehicleFlagsData'] !== null ? true : false;
          const isThereDataInsertedInRatingsPage =
            informationRegardingCurrentVin?.['vehicleRatingsData'] !== null ? true : false;
          const isThereDataInsertedInGalleryPage =
            informationRegardingCurrentVin?.['vehicleGalleryData'] !== null ? true : false;

          const currentlyUnlockedPwaVin = localStorage.getItem('currently-unlocked-pwa-vin');

          if (currentlyUnlockedPwaVin === null) {
            return {
              success: true,
              data: {
                information: isThereDataInsertedInInformationPage,
                options: isThereDataInsertedInOptionsPage,
                flags: isThereDataInsertedInFlagsPage,
                ratings: isThereDataInsertedInRatingsPage,
                gallery: isThereDataInsertedInGalleryPage,
                report: false,
                estimates: false
              }
            };
          } else {
            if (isThereDataInsertedInInformationPage) {
              const vin = informationRegardingCurrentVin?.['vehicleInformationData']?.['vinNumber'];

              if (vin === currentlyUnlockedPwaVin) {
                return {
                  success: true,
                  data: {
                    information: false,
                    options: false,
                    flags: false,
                    ratings: false,
                    gallery: false,
                    report: false,
                    estimates: false
                  }
                };
              } else {
                return {
                  success: true,
                  data: {
                    information: isThereDataInsertedInInformationPage,
                    options: isThereDataInsertedInOptionsPage,
                    flags: isThereDataInsertedInFlagsPage,
                    ratings: isThereDataInsertedInRatingsPage,
                    gallery: isThereDataInsertedInGalleryPage,
                    report: false,
                    estimates: false
                  }
                };
              }
            } else {
              return {
                success: true,
                data: {
                  information: false,
                  options: false,
                  flags: false,
                  ratings: false,
                  gallery: false,
                  report: false,
                  estimates: false
                }
              };
            }
          }
        }

        case '/car/list/estimator': {
          const objectToReturn = {
            success: true,
            data: [
              {
                id: 121,
                name: 'External Estimator Test',
                email: 'eet@uma.com',
                email_verified_at: null,
                status: true,
                city_id: '6091131feae0b',
                area_id: '60916142046bd',
                created_at: '2023-12-13T15:47:00.000000Z',
                updated_at: '2023-12-13T15:47:00.000000Z',
                mobile: '966531971998'
              },
              {
                id: 136,
                name: 'External Estimator Test -1',
                email: 'eet1@uma.com',
                email_verified_at: null,
                status: true,
                city_id: '6091131feae0b',
                area_id: '60916142046bd',
                created_at: '2023-12-14T15:40:23.000000Z',
                updated_at: '2024-02-19T12:52:35.000000Z',
                mobile: '966531971998'
              },
              {
                id: 137,
                name: 'External Estimator Test -2',
                email: 'eet2@uma.com',
                email_verified_at: null,
                status: true,
                city_id: '6091131feae0b',
                area_id: '60916142046bd',
                created_at: '2023-12-14T15:41:32.000000Z',
                updated_at: '2023-12-14T15:41:32.000000Z',
                mobile: '966531971998'
              }
            ]
          };

          return objectToReturn;
        }

        case '/ratings/get/parts-ratings': {
          /**
           * This API will give different response depending upon whether the `parent_id` key is provided in the body or not
           */

          const bodyTypeId = (httpRequest.body as any)['body_type_id'];
          const parentId = (httpRequest.body as any)['parent_id'];
          const partsConditionType = (
            (httpRequest.body as any)['parts_condition_type'] as string
          ).toLowerCase();

          let objectToReturn = {};

          if (parentId) {
            const tx = db.transaction('part_condition', 'readonly');
            const store = tx.objectStore('part_condition');

            const relevantInfo = await store.get(bodyTypeId);
            const relevantInfoForParticularPartsCondition =
              relevantInfo['partsConditions'][partsConditionType];

            for (let i = 0; i < relevantInfoForParticularPartsCondition.length; i++) {
              const data = relevantInfoForParticularPartsCondition[i];
              const ratingsData = data['ratings'][0];

              if (data?.['parts_condition_id'] === parentId) {
                objectToReturn = {
                  success: true,
                  data: {
                    child_parts_conditions: data?.['children'],
                    ratings: {
                      rating_id: ratingsData['rating_id'],
                      body_id: ratingsData['body_id'],
                      parts_condition_id: ratingsData['parts_condition_id'],
                      parts_condition_type: ratingsData['parts_condition_type'],
                      total_quality: ratingsData['total_quality'],
                      weightage: ratingsData['weightage'],
                      pqcf: ratingsData['pqcf'],
                      sl1: ratingsData['sl1'],
                      sl2: ratingsData['sl2'],
                      sl3: ratingsData['sl3'],
                      sl4: ratingsData['sl4'],
                      sl5: ratingsData['sl5'],
                      part_condition: {
                        parts_condition_id: data['parts_condition_id'],
                        parts_condition_type: data['parts_condition_type'],
                        parts_condition_name: data['parts_condition_name'],
                        parent_id: null
                      }
                    }
                  }
                };
              }
            }

            await tx.done;
          } else {
            const tx = db.transaction('part_condition', 'readonly');
            const store = tx.objectStore('part_condition');

            const relevantInfo = await store.get(bodyTypeId);

            const relevantInfoForParticularPartsCondition =
              relevantInfo['partsConditions'][partsConditionType];

            const neededData = [];

            for (let i = 0; i < relevantInfoForParticularPartsCondition.length; i++) {
              const data = relevantInfoForParticularPartsCondition[i];

              const ratingsData = data['ratings'][0];

              if (ratingsData !== undefined) {
                const obj = {
                  rating_id: ratingsData?.['rating_id'],
                  body_id: ratingsData?.['body_id'],
                  parts_condition_id: ratingsData?.['parts_condition_id'],
                  parts_condition_type: ratingsData?.['parts_condition_type'],
                  total_quality: ratingsData?.['total_quality'],
                  weightage: ratingsData?.['weightage'],
                  pqcf: ratingsData?.['pqcf'],
                  sl1: ratingsData?.['sl1'],
                  sl2: ratingsData?.['sl2'],
                  sl3: ratingsData?.['sl3'],
                  sl4: ratingsData?.['sl4'],
                  sl5: ratingsData?.['sl5'],
                  part_condition: {
                    parts_condition_id: data['parts_condition_id'],
                    parts_condition_type: data['parts_condition_type'],
                    parts_condition_name: data['parts_condition_name'],
                    parent_id: data['parent_id']
                  }
                };

                neededData.push(obj);
              }
            }

            tx.done;

            objectToReturn = {
              success: true,
              data: {
                child_parts_conditions: null,
                ratings: neededData
              }
            };
          }

          return objectToReturn;
        }

        case '/car/get/all/estimates': {
          const objectToReturn = {
            success: false,
            data: 'Data Not Found!'
          };

          return objectToReturn;
        }

        case '/ratings/get/ratings-states': {
          let objectToReturn = {};

          const data = await this._getInformationAboutTheCurrentVin();

          const vehicleRatingsData = data?.['vehicleRatingsData'];

          if (
            vehicleRatingsData === null ||
            vehicleRatingsData === undefined ||
            Object.keys(vehicleRatingsData).length === 0
          ) {
            objectToReturn = {
              success: true,
              data: {
                ratingStates: null
              }
            };
          } else {
            const vehicleInformationData = data?.['vehicleInformationData'];

            objectToReturn = {
              success: true,
              data: {
                ratingStates: {
                  rating_states_id: 'N/A',
                  vehicle_id: vehicleInformationData?.['vehicleId'] ?? 'N/A',
                  rating_states: JSON.stringify(vehicleRatingsData),
                  created_by: null,
                  updated_by: null,
                  created_at: null,
                  updated_at: null
                }
              }
            };
          }

          return objectToReturn;
        }

        case '/ratings/get/ratings-Conditions': {
          let objectToReturn = {};

          const parentPartId = (httpRequest.body as any)?.['parent_part_id'];
          const vehicleId = (httpRequest.body as any)?.['vehicle_id'];

          const informationRegardingCurrentVin = await this._getInformationAboutTheCurrentVin();

          const vehiclePartsRatingConditionData =
            informationRegardingCurrentVin?.['vehiclePartsRatingConditionsData'][parentPartId];

          if (vehiclePartsRatingConditionData) {
            const partsConditionsId = vehiclePartsRatingConditionData?.['parts_conditions_id'];
            const vpcsSeverity = vehiclePartsRatingConditionData?.['vpcs_severity'];

            let vpcsOldImage1 = undefined;
            let vpcsOldImage2 = undefined;
            let vpcsNewImage1 = undefined;
            let vpcsNewImage2 = undefined;

            if (vehiclePartsRatingConditionData?.['vpcs_old_image1']) {
              vpcsOldImage1 = await this._getImageFromImageStore({
                imageId: vehiclePartsRatingConditionData?.['vpcs_old_image1'],
                get: 'thumbnail'
              });
            }

            if (vehiclePartsRatingConditionData?.['vpcs_old_image2']) {
              vpcsOldImage2 = await this._getImageFromImageStore({
                imageId: vehiclePartsRatingConditionData?.['vpcs_old_image2'],
                get: 'thumbnail'
              });
            }

            if (vehiclePartsRatingConditionData?.['vpcs_new_image1']) {
              vpcsNewImage1 = await this._getImageFromImageStore({
                imageId: vehiclePartsRatingConditionData?.['vpcs_new_image1'],
                get: 'thumbnail'
              });
            }

            if (vehiclePartsRatingConditionData?.['vpcs_new_image2']) {
              vpcsNewImage2 = await this._getImageFromImageStore({
                imageId: vehiclePartsRatingConditionData?.['vpcs_new_image2'],
                get: 'thumbnail'
              });
            }

            const vpcsOldImageArrayData = [
              {
                1: vpcsOldImage1 ?? null
              },
              {
                2: vpcsOldImage2 ?? null
              }
            ];

            const vpcsNewImageArrayData = [
              {
                1: vpcsNewImage1 ?? null
              },
              {
                2: vpcsNewImage2 ?? null
              }
            ];

            objectToReturn = {
              success: true,
              data: {
                partsCondition: {
                  vpcs_id: 'N/A',
                  vehicle_id: vehicleId,
                  parent_part_id: parentPartId,
                  vpcs_conditions: partsConditionsId,
                  vpcs_severity: vpcsSeverity,
                  vpcs_s_dec: null,
                  vpcs_lb_cost: null,
                  vpcs_prt_cost: null,
                  vpcs_rp_cost: null,
                  vpcs_old_image: JSON.stringify(vpcsOldImageArrayData),
                  vpcs_new_image: JSON.stringify(vpcsNewImageArrayData),
                  vehicle_history: 'N/A',
                  created_by: null,
                  updated_by: null,
                  created_at: null,
                  updated_at: null
                }
              }
            };
          } else {
            objectToReturn = {
              success: true,
              data: {
                partsCondition: null
              }
            };
          }

          return objectToReturn;
        }

        case '/car/get/vehicle-gallery-data-info': {
          let objectToReturn = {};
          const informationRegardingCurrentVin = await this._getInformationAboutTheCurrentVin();
          const vehicleId = await this._getCurrentVehicleId();
          const vehicleGalleryData = informationRegardingCurrentVin?.['vehicleGalleryData'];

          /**
           * This response will be conditional. Fix this later.
           */

          const isThereGalleryData = Object.keys(vehicleGalleryData).length > 0;

          if (isThereGalleryData) {
            let vehicleGalleryDataArray = [];

            for (let k = 0; k < Object.entries(vehicleGalleryData).length; k++) {
              const relevantInfo = Object.entries(vehicleGalleryData)[k][1];
              const object = {
                part_id: relevantInfo?.['part_id'],
                part_name: relevantInfo?.['part_name'],
                image: await this._getImageFromImageStore({
                  imageId: relevantInfo?.['vehicle_gallery_data'],
                  get: 'thumbnail'
                })
              };
              vehicleGalleryDataArray.push(object);
            }

            objectToReturn = {
              success: true,
              data: {
                vehicle_gallery_data: JSON.stringify({
                  vehicle_id: vehicleId,
                  parts_images: vehicleGalleryDataArray
                })
              }
            };
          } else {
            objectToReturn = {
              success: true,
              data: {
                vehicle_gallery_data: null
              }
            };
          }

          return objectToReturn;
        }

        case '/car/get/duplicate-vin': {
          let objectToReturn = {
            success: true,
            data: {
              vehicle: [],
              vehicle_count: 0
            }
          };

          return objectToReturn;
        }

        default: {
          console.log('[961356]inside ResolveApiToResponseLocally.resolve::POST - default block');
          console.log('apiPath is:', apiPath);
        }
      }
    }
  }

  /**
   * The data structure that will hold the local data for PWA will of this shape
   * ```
   *
   * {
   *    vin: number,
   *    [userId1]: {
   *      apiCallsData: {
   *        registration: [],
   *        information: [],
   *        options: [],
   *        flags: [],
   *        ratings: {
   *          exterior: [],
   *          interior: [],
   *          mechanics: [],
   *          frame: [],
   *          majorOptions: []
   *        },
   *        gallery: []
   *      },
   *      localTempDataForPwa: {
   *        vehicleRegistrationData: {},
   *        vehicleInformationData: {},
   *        vehicleOptionsData: {},
   *        vehicleFlagsData: {},
   *        vehicleRatingsData: {},
   *        vehicleGalleryData: {},
   *      },
   *    },
   *    [userId2]: {
   *      ... (same shape as above)
   *    }
   * }
   *
   * ```
   */
  private async _setupTempDataStructureForThisUser(args: { vin?: string }) {
    let tempDataStructureForThisUser = {};

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();

    const defaultObjectToInsert = {
      apiCallsData: {
        registration: null,
        information: null,
        options: null,
        flags: null,
        ratings: {
          exterior: null,
          interior: null,
          mechanics: null,
          frame: null,
          major_options: null,
          generic: null
        },
        gallery: null
      },
      localTempData: {
        vehicleRegistrationData: null,
        vehicleInformationData: null,
        vehicleOptionsData: null,
        vehicleFlagsData: null,
        vehicleRatingsData: null,
        vehiclePartsRatingConditionsData: null,
        vehicleGalleryData: null,
        metadata: null
      },
      mapperData: null
    };

    /**
     * There might exist previously stored record with this VIN but for other user.
     */
    const possiblePreviousRecordsWithThisVin = await store.get(args.vin);

    // Case 1: No records exist with this VIN
    if (possiblePreviousRecordsWithThisVin === undefined) {
      tempDataStructureForThisUser = {
        vin: args.vin,
        [currentlyLoggedInUserId]: defaultObjectToInsert
      };

      await store.add(tempDataStructureForThisUser);
    }
    // Case 2: Some records exist with this VIN but for some other user
    else {
      tempDataStructureForThisUser = {
        ...possiblePreviousRecordsWithThisVin,
        [currentlyLoggedInUserId]: defaultObjectToInsert
      };

      await store.put(tempDataStructureForThisUser);
    }
    await tx.done;
  }

  private async _storeMapperForVin(args: {
    mapperValueObject: {
      [key: string]: {
        local: string | null;
        remote: string | null;
      };
    };
    vin?: string;
  }) {
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');

    const vinId = args.vin ?? this._getCurrentLocalVinId();

    /**
     * Here, we can have only two possibilities:
     *  1. some records exist with this VIN
     *  2. no records exist with this VIN
     */
    const possiblePreviousRecordsWithThisVin = await store.get(vinId);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();

    // no records exist with this VIN, this is the first time
    if (possiblePreviousRecordsWithThisVin === undefined) {
      const objectToInsertIntoDb = {
        vin_number: vinId,
        mapper: args.mapperValueObject
      };
      store.add(objectToInsertIntoDb);
    }
    // some records already exist with this VIN
    else {
      const objectToInsertIntoDb = {
        ...possiblePreviousRecordsWithThisVin,
        [currentlyLoggedInUserId]: {
          ...possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId],
          mapperData: {
            ...possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.['mapperData'],
            ...args.mapperValueObject
          }
        }
      };

      await store.put(objectToInsertIntoDb);
    }

    await tx.done;
  }

  private async _getInformationAboutTheCurrentVin() {
    const currentLocalVinId = localStorage.getItem('current-local-vin-id-for-pwa');
    if (currentLocalVinId == null) return undefined;
    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readonly');
    const store = tx.objectStore('VAM-PWA-API-CALLS');
    const data = await store.get(currentLocalVinId);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();
    return data?.[currentlyLoggedInUserId]?.['localTempData'];
  }

  private async _getAudioFromAudioStore(args: { audioId: string }) {
    const db = await this._getDatabase();
    const tx = db.transaction('image-store', 'readonly');
    const store = tx.objectStore('image-store');
    const data = await store.get(args.audioId);

    return data?.['dataUrl'];
  }

  private async _getVideoFromAudioStore(args: { videoId: string }) {
    const db = await this._getDatabase();
    const tx = db.transaction('image-store', 'readonly');
    const store = tx.objectStore('image-store');
    const data = await store.get(args.videoId);
    return data?.['dataUrl'];
  }

  private async _getImageFromImageStore(args: {
    imageId: string;
    get: 'image' | 'whole' | 'thumbnail';
  }) {
    const db = await this._getDatabase();
    const tx = db.transaction('image-store', 'readonly');
    const store = tx.objectStore('image-store');
    const data = await store.get(args.imageId);
    await tx.done;

    switch (args.get) {
      case 'whole': {
        return data;
      }
      case 'image': {
        return data?.['dataUrl'];
      }
      case 'thumbnail': {
        return data?.['thumbnailDataUrl'];
      }
    }
  }

  private async _getKeyValuePairsWhereValuesAreBinaryFile(value: unknown) {
    console.log('inside _getKeyValuePairsWhereValuesAreBinaryFile');

    let objectToReturn: Record<string, string> = {};

    if (value instanceof FormData) {
      console.log('instanceof FormData');

      for (let [k, v] of value) {
        console.log('k: ', k);
        console.log('v: ', v);
        console.log('-------------');

        if (this._isFile(v)) {
          console.log('file type: ', (v as File).type);

          const isImage = (v as File).type.startsWith('image/');
          objectToReturn[k] = await this._storeBinaryFile(v, isImage);
        }
      }
    }

    return objectToReturn;
  }

  /**
   * This method will store a binary file in the `image-store` object store and return that file's ID
   */
  private async _storeBinaryFile(file: any, isImage: boolean = true) {
    console.log('inside _storeBinaryFile', file);

    const id = generateRandomAlphanumericString();
    const dataUrl = await this._fileToDataUrl(file);

    console.log('dataUrl: ', dataUrl);

    const thumbnailDataUrl = isImage ? await this._generateThumbnailOfImage(file) : 'N/A';
    const db = await this._getDatabase();
    const tx = db.transaction('image-store', 'readwrite');
    const store = tx.objectStore('image-store');
    const currentLocalVinId = this._getCurrentLocalVinId();
    const objectToInsert = {
      id,
      dataUrl,
      thumbnailDataUrl,
      userId: this._getCurrentlyLoggedInUserId(),
      type: file.type,
      forVin: currentLocalVinId
    };
    console.log('objectToInsert: ', objectToInsert);

    await store.put(objectToInsert);
    await tx.done;
    return id;
  }

  private async _generateThumbnailOfImage(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = function (e) {
        const img = new Image();
        img.src = e.target.result as any;

        img.onload = function () {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set the new width and height for the thumbnail
          const maxWidth = 500; // Define the maximum width of the thumbnail
          const maxHeight = 500; // Define the maximum height of the thumbnail

          // Calculate the aspect ratio of the image
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxWidth) {
              height *= maxWidth / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width *= maxHeight / height;
              height = maxHeight;
            }
          }

          // Set the canvas size to the new dimensions
          canvas.width = width;
          canvas.height = height;

          ctx.drawImage(img, 0, 0, width, height);
          const thumbnailDataUrl = canvas.toDataURL('image/webp');
          resolve(thumbnailDataUrl);
        };

        img.onerror = function (error) {
          reject(error);
        };
      };

      reader.onerror = (error) => {
        reject(error);
      };
    });
  }

  private _isFile(data: unknown) {
    return data instanceof File;
  }

  private async _convertFormDataToJson(formData: FormData) {
    const jsonObjectToReturn: Record<string, unknown> = {};

    for (const [key, value] of formData.entries()) {
      jsonObjectToReturn[`${key}`] =
        value instanceof File ? await this._fileToDataUrl(value) : value;
    }

    return jsonObjectToReturn;
  }

  private async _convertFormDataToJsonV2(value: any) {
    const jsonObjectToReturn: Record<string, unknown> = {};
    if (value instanceof FormData) {
      for (const [k, v] of value.entries()) {
        jsonObjectToReturn[`${k}`] = v instanceof File ? 'N/A' : v;
      }
    }
    return jsonObjectToReturn;
  }

  private async _fileToDataUrl(file: File): Promise<string> {
    console.log('inside _fileToDataUrl', file);

    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        resolve(reader.result as string);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  private async _storeLocalTempData(args: {
    localTempDataValueObject: {
      [key in
        | 'vehicleRegistrationData'
        | 'vehicleInformationData'
        | 'vehicleOptionsData'
        | 'vehicleFlagsData'
        | 'vehicleRatingsData'
        | 'vehiclePartsRatingConditionsData'
        | 'vehicleGalleryData'
        | 'metadata']?: {
        [key: string]: unknown;
      };
    };
    vin?: string;
  }) {
    const vinId = args.vin ?? this._getCurrentLocalVinId();

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');

    const possiblePreviousRecordsWithThisVin = await store.get(vinId);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();

    let objectToInsertIntoDb = {
      ...(possiblePreviousRecordsWithThisVin ?? {})
    };

    for (const key in args.localTempDataValueObject) {
      const filteredData = Object.fromEntries(
        Object.entries(args.localTempDataValueObject[key]).filter(
          ([key, value]) => value !== undefined
        )
      );

      objectToInsertIntoDb = {
        ...objectToInsertIntoDb,
        [currentlyLoggedInUserId]: {
          ...(objectToInsertIntoDb?.[currentlyLoggedInUserId] ?? {}),
          localTempData: {
            ...(objectToInsertIntoDb?.[currentlyLoggedInUserId]?.['localTempData'] ?? {}),
            [key]: {
              ...(objectToInsertIntoDb?.[currentlyLoggedInUserId]?.['localTempData']?.[key] ?? {}),
              ...filteredData
            }
          }
        }
      };
    }

    await store.put(objectToInsertIntoDb);
    await tx.done;
  }

  private async _getCurrentVehicleId() {
    const informationRegardingCurrentVin = await this._getInformationAboutTheCurrentVin();
    return informationRegardingCurrentVin?.['vehicleRegistrationData']?.['vehicleId'] ?? -1;
  }

  private async _storeApiCallForVin(args: {
    httpRequest: HttpRequest<unknown>;
    endpoint: string;
    keysToBeReplacedInApiRequestBody?: Array<{ localKey: string; remoteKey: string }>;
    group: 'registration' | 'information' | 'options' | 'flags' | 'ratings' | 'gallery';
    keyValuePairsWhereValuesAreBinaryFiles?: Record<string, string>;
    requiresAuthToken?: boolean;
    isExecuted?: boolean;
    vin?: string;
  }) {
    const vinId = args.vin ?? this._getCurrentLocalVinId();
    const apiCallGroup = args.group;

    let body = {};

    if (args.httpRequest.body instanceof FormData && args.keyValuePairsWhereValuesAreBinaryFiles) {
      for (let [k, v] of args.httpRequest.body.entries()) {
        if (args.keyValuePairsWhereValuesAreBinaryFiles?.[k]) {
          body[k] = args.keyValuePairsWhereValuesAreBinaryFiles[k];
        } else {
          body[k] = v;
        }
      }
    } else {
      body = args.httpRequest.body;
    }

    const keysWhereValuesAreBinaryFiles = args.keyValuePairsWhereValuesAreBinaryFiles
      ? new Set(Object.entries(args.keyValuePairsWhereValuesAreBinaryFiles).map(([k, v]) => k))
      : null;

    const apiCallDetailsObject = {
      method: args.httpRequest.method,
      endpoint: args.endpoint,
      contentType:
        args.httpRequest.body instanceof FormData ? 'multipart/form-data' : 'application/json',
      requiresAuthToken: args.requiresAuthToken ?? true,
      isExecuted: args.isExecuted ?? false,
      // body: args.httpRequest.body instanceof FormData ? await this._convertFormDataToJson(args.httpRequest.body) : args.httpRequest.body,
      body: body,
      keysWhereValuesAreBinaryFiles
    };

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');

    /**
     * Here, we can have only two possibilities:
     *  1. some records exist with this VIN
     *  2. no records exist with this VIN
     */
    const possiblePreviousRecordsWithThisVin = await store.get(vinId);

    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();

    // no records exist with this VIN, this is the first time
    if (possiblePreviousRecordsWithThisVin === undefined) {
      const objectToInsertIntoDb = {
        vin_number: vinId,
        api_calls: {
          [apiCallGroup]: [
            {
              apiData: apiCallDetailsObject,
              keysToBeReplacedInApiRequestBody: args.keysToBeReplacedInApiRequestBody ?? null
            }
          ]
        }
      };
      store.add(objectToInsertIntoDb);
    }
    // some records already exist with this VIN
    else {
      const updatedObjectToInsertIntoDb = {
        ...possiblePreviousRecordsWithThisVin,
        [currentlyLoggedInUserId]: {
          ...possiblePreviousRecordsWithThisVin[currentlyLoggedInUserId],
          apiCallsData: {
            ...possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.['apiCallsData'],
            [apiCallGroup]: [
              ...(possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.['apiCallsData']?.[
                apiCallGroup
              ]
                ? [
                    ...possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.[
                      'apiCallsData'
                    ]?.[apiCallGroup]
                  ]
                : []),
              {
                apiData: apiCallDetailsObject,
                keysToBeReplacedInApiRequestBody: args.keysToBeReplacedInApiRequestBody ?? null
              }
            ]
          }
        }
        // api_calls: {
        //   ...possiblePreviousRecordsWithThisVin?.['api_calls'],
        //   [apiCallGroup]: [
        //     ...(possiblePreviousRecordsWithThisVin?.['api_calls']?.[apiCallGroup] === undefined
        //       ? []
        //       : [...possiblePreviousRecordsWithThisVin?.['api_calls']?.[apiCallGroup]]),
        //     {
        //       apiData: apiCallDetailsObject,
        //       forUser: this._getCurrentlyLoggedInUserId(),
        //       keysToBeReplacedInApiRequestBody: args.keysToBeReplacedInApiRequestBody ?? null
        //     }
        //   ]
        // }
      };

      await store.put(updatedObjectToInsertIntoDb);
    }

    await tx.done;
  }

  private async _storeApiCallForRatingsData(args: {
    httpRequest: HttpRequest<unknown>;
    endpoint: string;
    keysToBeReplacedInApiRequestBody?: Array<{ localKey: string; remoteKey: string }>;
    group:
      | 'exterior'
      | 'interior'
      | 'mechanics'
      | 'frame'
      | 'major_options'
      | 'generic'
      | 'grading'
      | 'repair_cost';
    requiresAuthToken?: boolean;
    keyValuePairsWhereValuesAreBinaryFiles?: Record<string, string>;
    isExecuted?: boolean;
    vin?: string;
  }) {
    const vinId = args.vin ?? this._getCurrentLocalVinId();
    const group = args.group;

    let body = {};

    if (args.httpRequest.body instanceof FormData && args.keyValuePairsWhereValuesAreBinaryFiles) {
      for (let [k, v] of args.httpRequest.body.entries()) {
        if (args.keyValuePairsWhereValuesAreBinaryFiles?.[k]) {
          body[k] = args.keyValuePairsWhereValuesAreBinaryFiles[k];
        } else {
          body[k] = v;
        }
      }
    } else {
      body = args.httpRequest.body;
    }

    const keysWhereValuesAreBinaryFiles = args.keyValuePairsWhereValuesAreBinaryFiles
      ? new Set(Object.entries(args.keyValuePairsWhereValuesAreBinaryFiles).map(([k, v]) => k))
      : null;

    const apiCallDetailsObject = {
      method: args.httpRequest.method,
      endpoint: args.endpoint,
      contentType:
        args.httpRequest.body instanceof FormData ? 'multipart/form-data' : 'application/json',
      requiresAuthToken: args.requiresAuthToken ?? false,
      isExecuted: args.isExecuted ?? false,
      body: body,
      keysWhereValuesAreBinaryFiles
    };

    const db = await this._getDatabase();
    const tx = db.transaction('VAM-PWA-API-CALLS', 'readwrite');
    const store = tx.objectStore('VAM-PWA-API-CALLS');

    const possiblePreviousRecordsWithThisVin = await store.get(vinId);
    const currentlyLoggedInUserId = this._getCurrentlyLoggedInUserId();

    if (possiblePreviousRecordsWithThisVin === undefined) {
      const objectToInsertIntoDb = {
        vin_number: vinId,

        api_calls: {
          ratings: {
            [group]: [
              {
                apiData: apiCallDetailsObject,
                forUser: this._getCurrentlyLoggedInUserId(),
                keysToBeReplacedInApiRequestBody: args.keysToBeReplacedInApiRequestBody ?? null
              }
            ]
          }
        }
      };
      store.add(objectToInsertIntoDb);
    } else {
      const updatedObjectToInsertIntoDb = {
        ...possiblePreviousRecordsWithThisVin,
        [currentlyLoggedInUserId]: {
          ...(possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId] ?? {}),
          apiCallsData: {
            ...(possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.['apiCallsData'] ??
              {}),
            ratings: {
              ...(possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.['apiCallsData']?.[
                'ratings'
              ] ?? {}),
              [group]: [
                ...(possiblePreviousRecordsWithThisVin?.[currentlyLoggedInUserId]?.[
                  'apiCallsData'
                ]?.['ratings']?.[group] ?? []),
                {
                  apiData: apiCallDetailsObject,
                  keysToBeReplacedInApiRequestBody: args.keysToBeReplacedInApiRequestBody ?? null
                }
              ]
            }
          }
        }
      };
      await store.put(updatedObjectToInsertIntoDb);
      await tx.done;
    }
  }

  private _setCurrentLocalVinId(vin: string): void {
    localStorage.setItem('current-local-vin-id-for-pwa', vin);
  }

  private _getCurrentLocalVinId() {
    return localStorage.getItem('current-local-vin-id-for-pwa');
  }

  private _getCurrentlyLoggedInUserId(): number {
    const userDataStoredInLocalStorage = localStorage.getItem('user');
    if (userDataStoredInLocalStorage === null) return -1;
    const parsedUserDataObject = JSON.parse(localStorage.getItem('user'));
    const possiblyFoundUserId = parsedUserDataObject?.['user']?.['user_data']?.['id'];
    return possiblyFoundUserId ?? -1;
  }
}
